<template>
  <v-container>
    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <v-row>
        <v-col cols="12">
          <v-card class="px-3 mt-4 pt-7">
            <v-row
              class="px-4"
            >
              <v-col cols="12">
                <SimplePageHeader
                  v-if="!editForm"
                  title="Add Permanent Placement"
                />
                <SimplePageHeader
                  v-else
                  title="Edit Permanent Placement"
                >
                </SimplePageHeader>
              </v-col>
            </v-row>
            <v-card-text>
              <v-tabs v-model="tab">
                <v-tab :key="BASIC_INFORMATION">
                  Basic Information
                </v-tab>
                <v-tab :key="PERMANENT_PLACEMENT">
                  Perm. Placement
                </v-tab>
                <v-tab :key="COMMISSIONS">
                  Commissions
                </v-tab>
                <v-tab :key="EXPENSES">
                  Expenses
                </v-tab>
                <v-tab :key="PAYMENTS_RECORDS" v-if="isEditForm">
                  Payment Records
                </v-tab>
              </v-tabs>
              <v-tabs-items v-model="tab">
                <v-tab-item :key="BASIC_INFORMATION">
                  <v-row class="my-5">
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="employeeName"
                        label="Employee Name"
                        :readonly="isReadOnlyAccess"
                        outlined
                        :rules="[required('Employee Name')]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <ClientsDropDownList
                        :id="clientId"
                        :isRequired="true"
                        :readonly="isReadOnlyAccess"
                        @clientIdChange="handleClientIdChange"
                      ></ClientsDropDownList>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item :key="PERMANENT_PLACEMENT">
                  <v-row class="mt-3">
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        label="Job Title"
                        v-model="jobTitle"
                        :readonly="isReadOnlyAccess"
                        outlined
                        :rules="[required('Job Title')]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <SimpleDatePicker
                        label="Placement Date"
                        :value="placementDate"
                        :isRequired="true"
                        :readonly="isReadOnlyAccess"
                        @selected-date-change="(newValue) => this.placementDate = newValue"
                      ></SimpleDatePicker>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <SimpleDatePicker
                        label="Invoice Date"
                        :value="invoiceDate"
                        :isRequired="false"
                        :readonly="isReadOnlyAccess"
                        @selected-date-change="(newValue) => this.invoiceDate = newValue"
                      ></SimpleDatePicker>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <SimpleDatePicker
                        label="Invoice Due Date"
                        :value="invoiceDueDate"
                        :isRequired="false"
                        :readonly="isReadOnlyAccess"
                        @selected-date-change="(newValue) => this.invoiceDueDate = newValue"
                      ></SimpleDatePicker>
                    </v-col>
                  </v-row>
                  <v-row class="mt-3 mb-5">
                    <v-col cols="12" sm="6" md="4">
                      <FormattedNumberField
                        label="Salary"
                        :value="salary"
                        :required="true"
                        :readonly="isReadOnlyAccess"
                        prepend-inner-icon="attach_money"
                        @valueChange="(newValue) => this.salary = newValue"
                      ></FormattedNumberField>
                    </v-col>
                    <v-col cols="8" sm="4" md="2">
                      <FormattedNumberField
                        label="Fee"
                        :value="fee"
                        :required="true"
                        :readonly="isReadOnlyAccess"
                        :prepend-inner-icon="feeTypeId === FIXED ?'attach_money' : null"
                        :rules="[isValidFee]"
                        :validate-on-blur="true"
                        @valueChange="feeChange"
                      ></FormattedNumberField>
                    </v-col>
                    <v-col cols="4" sm="2" md="1" class="pl-0 pt-0">
                      <v-radio-group
                        v-model="feeTypeId"
                        :readonly="isReadOnlyAccess"
                        :rules="[required('Fee Type')]"
                        @change="feeTypeIdChange"
                      >
                        <v-radio
                          label="%"
                          :value="PERCENTAGE"
                        ></v-radio>
                        <v-radio
                          label="Fixed"
                          :value="FIXED"
                        ></v-radio>
                      </v-radio-group>
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                      <FormattedNumberField
                        label="Net Fee"
                        :value="netFee"
                        :readonly="true"
                        prepend-inner-icon="attach_money"
                      ></FormattedNumberField>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <FormattedNumberField
                        label="Gross Profit"
                        :value="grossProfit"
                        :readonly="true"
                        :hint="grossProfitHint"
                        :persistent-hint="!!grossProfitHint"
                        prepend-inner-icon="attach_money"
                      ></FormattedNumberField>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item :key="COMMISSIONS">
                  <v-row class="my-5">
                    <v-col cols="12">
                      <CommissionsCollector
                        ref="commissionCollector"
                        commissionComponent="CommissionInput"
                        commissionType="commission"
                        :commissions="commissions"
                        :readonly="isReadOnlyAccess"
                        @commissionsChange="handleCommissionsChange"
                      ></CommissionsCollector>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item :key="EXPENSES">
                  <v-row class="my-5">
                    <v-col cols="12">
                      <ExpensesCard
                        class="px-2"
                        cardTitle="Expenses"
                        :expensesList="expenses"
                        :readonly="isReadOnlyAccess"
                        :showActive="false"
                        @expensesListChange="(newValue) => this.expenses = newValue"
                      ></ExpensesCard>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item :key="PAYMENTS_RECORDS" v-if="isEditForm">
                  <v-row class="my-5">
                    <v-col cols="12">
                      <div>
                        <v-row>
                          <v-col cols="12">
                            <v-card-title class="px-0">Payment Records</v-card-title>
                          </v-col>
                        </v-row>
                        <v-row v-if="!isReadOnlyAccess" justify="end">
                          <v-col cols="12" sm="6" lg="4" xl="3">
                            <v-btn block outlined color="primary" @click="addPaymentRecord">
                              Record Payment
                              <v-icon right>add</v-icon>
                            </v-btn>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <DirectPlacementPaymentsTable
                              :netFee="netFee"
                              :hide-actions-column="isReadOnlyAccess"
                              @editPayment="handleEditRecordPayment"
                              @paymentDeleted="handlePaymentDeleted"
                            />
                            <DirectPlacementPaymentDialog
                              ref="DirectPlacementPaymentDialog"
                              :netFee="netFee"
                              @saveRecordPayment="handleSaveRecordPayment"
                            />
                          </v-col>
                        </v-row>
                      </div>
                    </v-col>
                  </v-row>
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row justify="end" v-if="isReadOnlyAccess">
        <v-col cols="6" md="3" class="pl-2">
          <v-btn
            block
            outlined
            depressed
            @click="cancel"
          >
            Back
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <ButtonsSubmitForm
        v-if="!isReadOnlyAccess"
        :isFormValid="true"
        @save="handleSaveDirectPlacement"
        @cancel="cancel"
      >
      </ButtonsSubmitForm>
    <MessageBox
      ref="messageBox"
      message="There are required fields missing"
      color="error"
      closeTextColor="white"
    ></MessageBox>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { placementsRoute } from '@/lib/constants/routes'
import { SimpleDatePicker } from '@/components/shared/datePickers'
import { required } from '@/lib/formValidations'
import { ButtonsSubmitForm } from '@/components/buttons'
import { CommissionsCollector } from '@/components/commissions'
import { getTotalCommisions } from '@/lib/commissions'
import { ClientsDropDownList } from '@/components/clients'
import { SimplePageHeader } from '@/components/pageComponents'
import { DirectPlacementPaymentsTable, DirectPlacementPaymentDialog } from '@/components/directPlacementPayments'
import ExpensesCard from '@/components/expenses/ExpensesCard'
import { PERCENTAGE, FIXED } from '@/lib/constants/permPlacementFeeTypes'
import { formatCurrency } from '@/lib/display'
import { PRE_GP } from '@/lib/constants/expensesTypes'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { getCurrentUser, userHasRoles } from '@/lib/authentication'
import { FormattedNumberField } from '@/components/shared/textFields'
import { MessageBox } from '@/components/shared/messages'

export default {
  components: {
    SimpleDatePicker,
    ButtonsSubmitForm,
    CommissionsCollector,
    ClientsDropDownList,
    SimplePageHeader,
    DirectPlacementPaymentsTable,
    DirectPlacementPaymentDialog,
    ExpensesCard,
    FormattedNumberField,
    MessageBox
  },
  data: () => ({
    isFormValid: false,
    editForm: false,
    FIXED,
    PERCENTAGE,
    commissionAmount: 0,
    tab: null,
    BASIC_INFORMATION: 1,
    PERMANENT_PLACEMENT: 2,
    COMMISSIONS: 3,
    PAYMENTS_RECORDS: 4,
    EXPENSES: 5
  }),
  computed: {
    ...mapFields('directPlacement', [
      'id',
      'clientId',
      'clientName',
      'salary',
      'paymentReceived',
      'placementDate',
      'lastPaymentReceivedDate',
      'employeeName',
      'jobTitle',
      'isPaidInFull',
      'commissions',
      'payments',
      'expenses',
      'defaultExpensesByClient',
      'expensesDeduction',
      'expensesDeductionHint',
      'feeTypeId',
      'invoiceDate',
      'invoiceDueDate',
      'fee'
    ]),
    ...mapFields('directPlacementPayment', [
      'directPlacementId'
    ]),
    currentDirectPlacementId: function () {
      return this.$route.params.id
    },
    isReadOnlyAccess: {
      get () {
        return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, SALES_PERSON, MANAGER] })
      }
    },
    isEditForm () {
      return this.$route.params.id > 0
    },
    netFee () {
      return this.salary && this.fee && this.feeTypeId
        ? (this.feeTypeId === PERCENTAGE
          ? (this.salary * this.fee) / 100 : this.fee)
        : null
    },
    grossProfit () {
      if (this.netFee) {
        return (this.netFee - this.expensesDeduction).toFixed(2)
      } else {
        return null
      }
    },
    grossProfitHint () {
      if (this.netFee) {
        let hint = `Gross Profit = Net Fee (${formatCurrency(this.netFee)})`
        return this.expensesDeduction > 0 ? `${hint} - ${this.expensesDeductionHint}` : hint
      } else {
        return null
      }
    }
  },
  watch: {
    defaultExpensesByClient () {
      // First delete the default expenses
      if (this.expenses && this.expenses.length > 0) {
        // Look for the client's default expenses
        let defaultExpenses = this.expenses.filter(expense => !!expense.clientDefaultExpense)
        for (let defaultExpense of defaultExpenses) {
          let indexToDelete = this.expenses.findIndex(expense => expense.id === defaultExpense.id && !!expense.clientDefaultExpense)
          this.expenses.splice(indexToDelete, 1)
        }
      }
      // Then include the default expenses of the existing client chosen
      for (let index = this.defaultExpensesByClient.length - 1; index >= 0; index--) {
        this.expenses.unshift({ ...this.defaultExpensesByClient[index], clientDefaultExpense: true })
      }
    },
    expenses () {
      // Set expense deduction
      this.expensesDeduction = 0
      this.expensesDeductionHint = ''
      this.expenses.map((expense) => {
        if (expense.expenseTypeId === PRE_GP) {
          this.expensesDeduction += parseFloat(expense.amount)
          this.expensesDeductionHint += `${expense.expenseName} (${formatCurrency(expense.amount)}) - `
        }
      })
      this.expensesDeductionHint = this.expensesDeductionHint.substring(0, this.expensesDeductionHint.length - 3)
    }
  },
  methods: {
    getCurrentUser,
    userHasRoles,
    required,
    getTotalCommisions,
    ...mapActions('directPlacement', [
      'getDirectPlacement',
      'saveDirectPlacement',
      'clearDirectPlacement'
    ]),
    ...mapActions('directPlacementPayment', [
      'getDirectPlacementPayment',
      'saveDirectPlacementPayment',
      'clearDirectPlacementPayment'
    ]),
    ...mapActions('directPlacementPayments', [
      'getDirectPlacementsPayments'
    ]),
    ...mapActions('client', ['getClient']),
    loadDirectPlacementData () {
      this.getDirectPlacement({ id: this.currentDirectPlacementId })
    },
    checkRequiredFieds () {
      return this.clientId &&
      this.employeeName &&
      this.jobTitle &&
      this.placementDate &&
      this.salary &&
      this.feeTypeId &&
      this.fee
    },
    handleSaveDirectPlacement () {
      if (this.$refs.form.validate() &&
      this.checkRequiredFieds() &&
      (this.$refs.commissionCollector ? this.$refs.commissionCollector.validateCommissionsPercentages() : true)) {
        this.saveDirectPlacement().then(() => {
          this.$router.push({ name: placementsRoute.name })
        })
      } else {
        this.$refs.messageBox.show()
      }
    },
    handleClientIdChange (value) {
      this.clientId = value
      if (this.clientId) {
        this.getClient({ id: this.clientId }).then((client) => {
          this.defaultExpensesByClient = client.directPlacementsExpenses.filter(expense => expense.active === true)
        })
      }
    },
    handleCommissionsChange (value) {
      this.commissions = value
    },
    cancel () {
      this.$router.push({ name: placementsRoute.name })
    },
    addPaymentRecord () {
      this.clearDirectPlacementPayment()
      this.$refs.DirectPlacementPaymentDialog.showModal()
    },
    handleSaveRecordPayment () {
      this.directPlacementId = this.currentDirectPlacementId
      this.saveDirectPlacementPayment().then(() => {
        this.clearDirectPlacementPayment()
        this.getDirectPlacementsPayments({ directPlacementId: this.currentDirectPlacementId })
      })
    },
    handleEditRecordPayment (paymentId) {
      this.getDirectPlacementPayment({ id: paymentId }).then(() => {
        this.$refs.DirectPlacementPaymentDialog.showModal()
      })
    },
    isValidFee () {
      return this.fee && this.feeTypeId
        ? (this.feeTypeId === PERCENTAGE
          ? (parseInt(this.fee) <= 100 ? true : 'The fee percent is not valid.') : true) : true
    },
    feeChange (newValue) {
      this.fee = newValue
      if (this.fee && !this.feeTypeId) {
        if (this.fee > 100) {
          this.feeTypeId = FIXED
        } else {
          this.feeTypeId = PERCENTAGE
        }
      }
    },
    feeTypeIdChange () {
      if (this.fee && this.fee > 100 && this.feeTypeId === PERCENTAGE) {
        this.fee = null
      }
      this.$refs.form.resetValidation()
    },
    handlePaymentDeleted () {
      this.getDirectPlacementsPayments({ directPlacementId: this.currentDirectPlacementId })
    }
  },
  created () {
    if (this.currentDirectPlacementId) {
      this.editForm = true
      this.loadDirectPlacementData()
      this.getDirectPlacementsPayments({ directPlacementId: this.currentDirectPlacementId })
    } else {
      this.editForm = false
      this.clearDirectPlacement()
    }
  }
}
</script>
